/**
 * Realtime hook that keeps every org member's Tasks board live when a task's
 * VISIBILITY changes elsewhere - `is_private` flips, or assignees change. A user
 * who LOSES access cannot receive a `postgres_changes` event (RLS won't deliver
 * a row they can no longer SELECT), so visibility changes arrive as a
 * content-free broadcast "poke" on a per-org channel and the board does a
 * DEBOUNCED refetch; RLS re-gates what survives. See tasks-sync-channel.ts for
 * the security invariant and docs/plans/pub-893-task-sharing-redo.md.
 *
 * The channel is private (defense in depth - only org members can join) but
 * correctness does NOT depend on that: it depends on the payload being
 * content-free and RLS re-gating the refetch.
 *
 * MOUNTED AT APP ROOT (via {@link TasksSyncRealtimeMount} in App.tsx), not just
 * on the Tasks board. The channel doubles as the SENDER channel for
 * `broadcastTasksChanged`: an actor who mutates a task's visibility while OFF the
 * board (from a detail panel, a correspondence view, a quick action) has no board
 * channel joined, so the poke would be dropped and other members' boards would
 * never refresh (RC-3). Keeping the subscription alive at app root guarantees the
 * actor always has a JOINED channel to broadcast from, independent of where they
 * are in the app. When a board IS open it mounts the same hook too; the per-org
 * `Set` registry in tasks-sync-channel.ts tolerates the two concurrent channels.
 * The actor never receives its OWN poke: Supabase `broadcast.self` defaults to
 * `false`, so the sending client gets no echo (zero self-pokes, not one coalesced
 * away). The actor's own board refreshes via its mutation's cache invalidation.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/auth-context';
import { useOrganization } from '@/contexts/organization-context';
import { useRealtimeChannel } from '@/hooks/use-realtime-channel';
import { TASK_QUERY_KEYS } from '@/hooks/task-query-keys';
import { isQueryableOrganizationId } from '@/lib/organization-id';
import { reportingKeys } from '@/hooks/use-reporting-query-keys';
import {
  TASKS_CHANGED_EVENT,
  buildTasksSyncTopic,
  registerTasksSyncChannel,
  unregisterTasksSyncChannel,
} from '@/hooks/tasks-sync-channel';

import type { ReactElement } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';

// Coalesce a burst of visibility mutations (e.g. a rapid private toggle, or
// several assignee edits) into a single refetch.
const REFETCH_DEBOUNCE_MS = 500;

// Swallow an invalidation rejection — a torn-down query client is not an error
// worth surfacing here. Mirrors use-task-share-revocation-realtime.
const swallow = () => false;

interface UseTasksSyncRealtimeOptions {
  readonly enabled?: boolean;
  /** Test seam: invoked after the debounced refetch fires. */
  readonly onRefetched?: () => void;
}

/**
 * Subscribe a Tasks board to the org-wide tasks-sync poke channel.
 *
 * `orgId` is passed explicitly so the channel scopes to the board's org. No-ops
 * (subscription disabled) without an org and an authenticated user.
 */
export function useTasksSyncRealtime(
  orgId: string | undefined,
  options: UseTasksSyncRealtimeOptions = {},
): void {
  const { enabled = true, onRefetched } = options;
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id;
  // isQueryableOrganizationId (not bare truthiness): the platform-admin
  // 'admin' sentinel would otherwise join a `tasks-sync:admin` topic that
  // realtime RLS rejects, feeding repeated denials to Sentry.
  const active = isQueryableOrganizationId(orgId) && !!userId && enabled;

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // Bind (orgId, channel) together at registration so teardown unregisters
  // exactly what was registered — never a later render's orgId. Without this,
  // an org switch (or a deferred null interleaving) could leak the old org's
  // channel in the registry or unregister the live one. Mirrors how
  // unregisterCommentRoomChannel keys teardown on the value captured at
  // registration, not a mutable ref read later.
  const registeredRef = useRef<{ orgId: string; channel: RealtimeChannel } | null>(null);

  // Stash the test-seam callback so the debounce timer reads the latest closure
  // without re-subscribing the channel. Synced in an effect (not during render)
  // and called from the timer (not an effect/event handler), so neither
  // react-hooks/refs nor rules-of-hooks (useEffectEvent) applies here.
  const onRefetchedRef = useRef(onRefetched);
  useEffect(() => {
    onRefetchedRef.current = onRefetched;
  }, [onRefetched]);

  // Clear any pending debounce on unmount so a settled timer can't invalidate
  // against a torn-down board.
  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = undefined;
      }
    },
    [],
  );

  // Debounced board refetch, shared by the content-free poke and the
  // reconnect catch-up. Debounced so a burst of pokes coalesces into one
  // refetch; RLS re-gates visibility on the refetch (a now-private /
  // now-unassigned task drops out for a member who lost access).
  const scheduleBoardRefetch = (): void => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = undefined;
      if (!orgId) {
        return;
      }
      // Invalidate THIS org's board (scoped to [tasks, orgId] so a multi-org
      // member's other orgs don't refetch) plus the user's my-tasks feeds.
      queryClient
        .invalidateQueries({
          queryKey: [TASK_QUERY_KEYS.tasks, orgId],
          refetchType: 'all',
        })
        .catch(swallow);
      // Also re-run any cached task detail so a member viewing a task that just
      // went private / was unassigned re-fetches under RLS (and sees "not found"
      // instead of stale gov data) rather than only the board dropping it. The
      // single-task key is [task, taskId, orgId] (orgId last), so it can't be
      // org-prefix-scoped like the board; match the [task] prefix and scope to
      // THIS org via a predicate on the trailing segment so a multi-org member's
      // other-org details don't churn.
      queryClient
        .invalidateQueries({
          queryKey: [TASK_QUERY_KEYS.task],
          predicate: (query) => query.queryKey[2] === orgId,
          refetchType: 'all',
        })
        .catch(swallow);
      queryClient
        .invalidateQueries({ queryKey: [TASK_QUERY_KEYS.myTasks], refetchType: 'all' })
        .catch(swallow);
      queryClient
        .invalidateQueries({
          queryKey: [TASK_QUERY_KEYS.myTasksByOrg],
          refetchType: 'all',
        })
        .catch(swallow);
      // Reporting tiles aggregate the same rows. This is the only invalidation
      // that can fire while a Reporting tab is cached but Activity-hidden, so it
      // keeps the default `refetchType: 'active'`: a visible dashboard refetches
      // now, a hidden one is marked and picked up by the reveal catch-up in
      // `tab-content-renderer`. `'all'` would refetch every hidden tab's widgets
      // and defeat that design. Org-scoped via the key's orgId segment.
      queryClient
        .invalidateQueries({ queryKey: [...reportingKeys.widgetData(), orgId] })
        .catch(swallow);
      onRefetchedRef.current?.();
    }, REFETCH_DEBOUNCE_MS);
  };

  useRealtimeChannel({
    channelName: active ? buildTasksSyncTopic(orgId) : 'tasks-sync',
    channelConfig: { config: { private: true } },
    broadcastSubscriptions: active
      ? [
          {
            event: TASKS_CHANGED_EVENT,
            // Content-free poke: we deliberately ignore the payload.
            onPayload: () => scheduleBoardRefetch(),
          },
        ]
      : [],
    // On a reconnect (backoff retry or post-token-refresh recovery) the poke
    // that fired while the socket was down was lost — the global query client
    // does not refetch-on-reconnect — so catch up the same board surfaces. Not
    // fired on the initial join. See use-realtime-channel.ts.
    onResubscribed: active ? () => scheduleBoardRefetch() : undefined,
    enabled: active,
    onChannel: (channel) => {
      if (channel && orgId) {
        registeredRef.current = { orgId, channel };
        registerTasksSyncChannel(orgId, channel);
        return;
      }
      // channel === null: the hook tore the channel down. Unregister the exact
      // (orgId, channel) pair captured at registration so the actor never
      // broadcasts to a dead socket and an org switch can't leak the old
      // channel.
      if (registeredRef.current) {
        unregisterTasksSyncChannel(registeredRef.current.orgId, registeredRef.current.channel);
        registeredRef.current = null;
      }
    },
  });
}

/**
 * App-root mount for the tasks-sync channel (rendered in App.tsx, inside the
 * organization provider). Keeps the current org's sender channel JOINED at all
 * times so `broadcastTasksChanged` reaches other members even when the actor is
 * NOT on a Tasks board (RC-3: an off-board visibility mutation must still refresh
 * everyone else's board). Renders nothing.
 *
 * Reads the org from `currentOrganizationId` and passes an org only when there is
 * a real one: the guard suppresses both `null` (no active org) AND the `'admin'`
 * platform-admin sentinel (see organization-provider.tsx), so admin mode does not
 * join a pointless `tasks-sync-admin` channel — the hook no-ops in both cases. The
 * Tasks board mounts the same hook when open; the per-org `Set` registry tolerates
 * both channels coexisting.
 */
export function TasksSyncRealtimeMount(): ReactElement | null {
  const { currentOrganizationId } = useOrganization();
  const orgId =
    currentOrganizationId && currentOrganizationId !== 'admin' ? currentOrganizationId : undefined;
  useTasksSyncRealtime(orgId);
  return null;
}
